// ---- Structure registry ----
// Where walkable buildings are placed in the world. Unlike scattered decos,
// structures are placed by hand at fixed spots (like the cabins/wells landmarks)
// because they define interiors, collision, and walkable floors. Each entry
// reserves a footprint radius so deco scatter stays clear of it.
//
// PLACEMENTS are content (map-doc `structures[]` — plan 21; hydrated by
// src/content/load.ts). Geometry is mostly content/structures/*.json via
// interpretStructure; only castle stays coded. This module binds ids → factories.

import { defineStructure, type StructureSpec } from "./define.ts";
import castle from "./castle.ts";
import { loadedStructurePlacements, loadedStructureDocs } from "../content/load.ts";
import { interpretStructure } from "./doc.ts";
import { parseHex } from "../model/parts.ts";
import { variantSeed, seededUnit } from "../model/variants.ts";
import type { StructureDoc } from "../content/schemas.ts";

export type StructurePlacement = {
  spec: StructureSpec;
  x: number;
  z: number;
  /** Quarter-turns about Y (0–3 → 0/90/180/270°). Lets one structure be reused at
   *  different orientations. Defaults to 0. */
  turns?: number;
  /** World height of the structure's feet. Defaults to the (flattened) terrain
   *  height at (x,z), so the building sits ON the ground rather than at y=0. */
  baseY?: number;
  /** Keep-out radius for deco scatter. Defaults to the structure's footprint
   *  (half-diagonal of its interior AABB + a margin), auto-derived at build time. */
  footprint?: number;
  /** Flatten pad override. By default buildWorld levels a disc of radius
   *  `footprint` to `baseY - 0.12` (grass tucks just under the stonework) with a
   *  wide feather — matching the castle's hand-tuned pad. Override any field. */
  flatten?: { r?: number; feather?: number; y?: number };
};

/** Data-driven building kinds (content/structures/*.json). A placement without
 *  params shares the doc's default-bound spec; a placement WITH params gets its
 *  own spec whose build resolves the doc's expressions with those overrides. */
const DOC_MAP = new Map(loadedStructureDocs.map((doc) => [doc.id, doc]));

function docSpec(doc: (typeof loadedStructureDocs)[number], params?: Record<string, number>): StructureSpec {
  return defineStructure({
    type: doc.id,
    mapColor: parseHex(doc.mapColor)!, // schema-validated #rrggbb
    build: (ctx) => interpretStructure(ctx, doc, params),
  });
}

/** Pick the VARIATION NAME for a placement. Explicit `p.variant` wins; otherwise
 *  seed-pick one from the doc's variants weighted by `weight`, keyed on the
 *  placement position so it's deterministic + stable across client/server/studio
 *  (same idea as the model `variation` seed). undefined ⇒ the doc has no variants
 *  (build its own defaults). */
function pickVariantName(
  doc: StructureDoc,
  p: (typeof loadedStructurePlacements)[number],
): string | undefined {
  const variants = doc.variants;
  if (!variants) return undefined;
  const names = Object.keys(variants).sort(); // sorted = deterministic pick order
  if (names.length === 0) return undefined;
  if (p.variant) return p.variant;

  const weights = names.map((n) => variants[n]!.weight ?? 1);
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = seededUnit(variantSeed(`${p.spec}:${p.x}:${p.z}`), 0) * total;
  for (let i = 0; i < names.length; i++) {
    roll -= weights[i]!;
    if (roll < 0) return names[i]!;
  }
  return names[names.length - 1]!;
}

/** Merge a named VARIATION's params + palette into `doc` and build its spec. The
 *  ONE place that knows how a variation layers over its style — shared by world
 *  placement (specFor) and the studio catalog (variation browsing). `variant`
 *  undefined ⇒ the doc's own defaults; `params` override the variation's params. */
export function buildVariantSpec(
  doc: StructureDoc,
  variant?: string,
  params?: Record<string, number>,
): StructureSpec {
  const v = variant ? doc.variants?.[variant] : undefined;
  // Variation params + caller params (caller wins). Empty → no overrides, so a
  // param-less variation resolves the doc against its own defaults.
  const merged = { ...v?.params, ...params };
  // A variation may recolor (shallow palette merge) without changing construction.
  const effectiveDoc = v?.palette
    ? { ...doc, palette: { ...doc.palette, ...v.palette } as StructureDoc["palette"] }
    : doc;
  return docSpec(effectiveDoc, Object.keys(merged).length ? merged : undefined);
}

/** Resolve a RAW placement record (spec as a string id, as authored in the map
 *  doc) to its buildable StructureSpec — coded castle or a style doc +
 *  variant/params. Exported for the studio world editor, which builds EDITED
 *  placements through the same resolution the game uses. */
export function placementSpec(p: (typeof loadedStructurePlacements)[number]): StructureSpec {
  return specFor(p);
}

function specFor(p: (typeof loadedStructurePlacements)[number]): StructureSpec {
  if (p.spec === "castle") return castle;

  const doc = DOC_MAP.get(p.spec);
  if (!doc) throw new Error(`unknown structure spec "${p.spec}"`); // validation catches this pre-boot

  return buildVariantSpec(doc, pickVariantName(doc, p), p.params as Record<string, number> | undefined);
}

/** Resolve a map doc's raw `structures` list to buildable placements — the
 *  per-map twin of STRUCTURE_PLACEMENTS (plan 21: buildWorld takes a map doc). */
export function resolvePlacements(
  raw: typeof loadedStructurePlacements,
): StructurePlacement[] {
  return raw.map((p) => ({
    spec: specFor(p),
    x: p.x,
    z: p.z,
    turns: p.turns,
    baseY: p.baseY,
    footprint: p.footprint,
    flatten: p.flatten,
  }));
}

/** The DEFAULT map's placements (single-map callers: studio catalog, tests). */
export const STRUCTURE_PLACEMENTS: StructurePlacement[] = resolvePlacements(loadedStructurePlacements);
